import { apiService } from './api';
import type { VoiceThemeSettings } from '../utils/voiceThemeProcessing';

export interface VoiceStylePreset {
  id: string;
  name: string;
  icon: string;
  description?: string;
  settings: VoiceThemeSettings;
  displayOrder: number;
}

export const VOICE_STYLIZATION_PRESETS_UPDATED_EVENT = 'voice-stylization:presets-updated';

export function mapApiVoiceStylePreset(raw: Record<string, unknown>): VoiceStylePreset {
  const settingsRaw = (raw.settings ?? raw.Settings ?? {}) as Record<string, unknown>;
  return {
    id: String(raw.id ?? raw.Id ?? ''),
    name: String(raw.name ?? raw.Name ?? ''),
    icon: String(raw.icon ?? raw.Icon ?? 'Mic'),
    description: raw.description != null ? String(raw.description) : raw.Description != null ? String(raw.Description) : undefined,
    displayOrder: Number(raw.displayOrder ?? raw.DisplayOrder ?? 0),
    settings: {
      pitchSemitones: Number(settingsRaw.pitchSemitones ?? settingsRaw.PitchSemitones ?? 0),
      chorusMix: Number(settingsRaw.chorusMix ?? settingsRaw.ChorusMix ?? 0),
      gain: Number(settingsRaw.gain ?? settingsRaw.Gain ?? 1),
      bassDb: Number(settingsRaw.bassDb ?? settingsRaw.BassDb ?? 0),
      trebleDb: Number(settingsRaw.trebleDb ?? settingsRaw.TrebleDb ?? 0),
      ...(settingsRaw.presenceHz != null || settingsRaw.PresenceHz != null
        ? { presenceHz: Number(settingsRaw.presenceHz ?? settingsRaw.PresenceHz) }
        : {}),
      ...(settingsRaw.presenceDb != null || settingsRaw.PresenceDb != null
        ? { presenceDb: Number(settingsRaw.presenceDb ?? settingsRaw.PresenceDb) }
        : {}),
      ...(settingsRaw.bandpassHz != null || settingsRaw.BandpassHz != null
        ? { bandpassHz: Number(settingsRaw.bandpassHz ?? settingsRaw.BandpassHz) }
        : {}),
      ...(settingsRaw.bandpassQ != null || settingsRaw.BandpassQ != null
        ? { bandpassQ: Number(settingsRaw.bandpassQ ?? settingsRaw.BandpassQ) }
        : {}),
      ...(settingsRaw.distortion != null || settingsRaw.Distortion != null
        ? { distortion: Number(settingsRaw.distortion ?? settingsRaw.Distortion) }
        : {}),
      ...(settingsRaw.reverbMix != null || settingsRaw.ReverbMix != null
        ? { reverbMix: Number(settingsRaw.reverbMix ?? settingsRaw.ReverbMix) }
        : {}),
    }
  };
}

class VoiceStylePresetService {
  private cache: VoiceStylePreset[] | null = null;
  private readonly endpoint = '/api/Admin/system-settings/voice-stylization';

  async getPresets(forceRefresh = false): Promise<VoiceStylePreset[]> {
    if (this.cache && !forceRefresh) {
      return this.cache;
    }
    const response = await apiService.get<unknown[]>(this.endpoint, { cache: false });
    const presets = (response ?? []).map((item) => mapApiVoiceStylePreset(item as Record<string, unknown>));
    this.cache = presets.sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
    return this.cache;
  }

  async savePresets(presets: VoiceStylePreset[]): Promise<VoiceStylePreset[]> {
    const response = await apiService.post<{ presets?: unknown[] }>(this.endpoint, presets, { cache: false });
    const saved = (response?.presets ?? presets).map((item) =>
      mapApiVoiceStylePreset(item as Record<string, unknown>)
    );
    this.cache = saved;
    apiService.clearCache();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(VOICE_STYLIZATION_PRESETS_UPDATED_EVENT, { detail: saved })
      );
    }
    return saved;
  }

  clearCache() {
    this.cache = null;
  }

  findPreset(presets: VoiceStylePreset[], presetId: string | null | undefined): VoiceStylePreset | undefined {
    if (!presetId || presetId === 'original') return undefined;
    return presets.find((p) => p.id === presetId);
  }
}

export const voiceStylePresetService = new VoiceStylePresetService();
